// Sachbearbeiter-Workload — Betriebsleiter-Sicht
// Zeigt pro Sachbearbeiter: offene Aufträge + überfällig + Stunden
import { Users, AlertTriangle } from 'lucide-react';
import type { SachbearbeiterWorkload } from '../lib/api';

export default function SachbearbeiterWorkloadWidget({
  data,
}: {
  data: SachbearbeiterWorkload[] | undefined;
}) {
  if (!data || data.length === 0) {
    return (
      <section className="bg-white rounded-xl border border-slate-200 p-5 mb-6">
        <h2 className="font-semibold flex items-center gap-2 mb-3">
          <Users className="w-4 h-4 text-blue-600" />
          Sachbearbeiter-Workload
        </h2>
        <div className="text-sm text-slate-500">Keine zugeordneten Aufträge</div>
      </section>
    );
  }

  const total = data.reduce((s, sb) => s + sb.offen, 0);
  const totalUe = data.reduce((s, sb) => s + sb.ueberfaellig, 0);
  const max = Math.max(...data.map(sb => sb.offen));

  return (
    <section className="bg-white rounded-xl border border-slate-200 p-5 mb-6">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold flex items-center gap-2">
          <Users className="w-4 h-4 text-blue-600" />
          Sachbearbeiter-Workload
          <span className="text-slate-400 font-normal">({data.length})</span>
        </h2>
        <div className="text-xs text-slate-500">
          <span className="font-semibold text-slate-700">{total}</span> offen gesamt
          {totalUe > 0 && (
            <span className="ml-2 text-red-600 font-semibold">· {totalUe} überfällig</span>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs text-slate-500 uppercase border-b border-slate-200">
            <tr>
              <th className="text-left px-2 py-2 font-medium">Sachbearbeiter</th>
              <th className="text-right px-2 py-2 font-medium">Offen</th>
              <th className="text-right px-2 py-2 font-medium">Überfällig</th>
              <th className="text-right px-2 py-2 font-medium">Stunden</th>
              <th className="text-right px-2 py-2 font-medium w-2/5">Verteilung</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.map(sb => {
              const pct = max > 0 ? (sb.offen / max) * 100 : 0;
              const uePct = sb.offen > 0 ? (sb.ueberfaellig / sb.offen) * 100 : 0;
              return (
                <tr key={sb.user_id} className="hover:bg-slate-50">
                  <td className="px-2 py-2">
                    <span className="font-medium">{sb.name}</span>
                    {sb.kurzel && (
                      <span className="ml-2 text-xs text-slate-400">[{sb.kurzel}]</span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums font-semibold">{sb.offen}</td>
                  <td className="px-2 py-2 text-right tabular-nums">
                    {sb.ueberfaellig > 0 ? (
                      <span className="inline-flex items-center gap-1 text-red-700 font-semibold">
                        <AlertTriangle className="w-3 h-3" />
                        {sb.ueberfaellig}
                      </span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums text-slate-600">
                    {sb.stunden_offen.toFixed(1)}h
                    {sb.stunden_ueberfaellig > 0 && (
                      <span className="text-red-600 ml-1 text-xs">
                        ({sb.stunden_ueberfaellig.toFixed(0)}h ü)
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-slate-100 rounded overflow-hidden relative">
                        <div
                          className="absolute inset-y-0 left-0 bg-blue-500 rounded-l"
                          style={{ width: `${pct}%` }}
                        />
                        {uePct > 0 && (
                          <div
                            className="absolute inset-y-0 bg-red-500"
                            style={{
                              left: `${pct - (pct * uePct) / 100}%`,
                              width: `${(pct * uePct) / 100}%`,
                            }}
                          />
                        )}
                      </div>
                      <span className="text-[10px] text-slate-400 tabular-nums w-8 text-right">
                        {Math.round(pct)}%
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-3 text-[11px] text-slate-400 flex items-center gap-3">
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-3 h-2 bg-blue-500 rounded-sm" /> Offen
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-3 h-2 bg-red-500 rounded-sm" /> Überfällig
        </span>
        <span>· Aufträge aus allen 4 Modulen (Drückteile + Laufräder + Baugruppen + Montage)</span>
      </div>
    </section>
  );
}
